import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import JsSIP from "jssip";
import { format } from "date-fns";
import {
  Signal, SignalZero, PhoneIncoming, Copy, Hash, Server, Building2,
  PhoneCall, History, Zap, Smartphone,
} from "lucide-react";
import { api, formatApiError, wsUrl } from "../lib/api";
import { Shell } from "../components/Shell";
import { IncomingCallModal } from "../components/IncomingCallModal";
import { Button } from "../components/ui/button";

const STATUS_STYLES = {
  answered: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  completed: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  door_unlocked: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  rejected: "text-red-400 bg-red-500/10 border-red-500/30",
  missed: "text-slate-400 bg-slate-500/10 border-slate-500/30",
  ringing: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
};

export default function TenantPortal() {
  const [ext, setExt] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [calls, setCalls] = useState([]);
  const [wsStatus, setWsStatus] = useState("connecting"); // connecting | online | offline
  const [incoming, setIncoming] = useState(null);
  const [inCall, setInCall] = useState(false);
  const wsRef = useRef(null);
  const incomingRef = useRef(null);
  incomingRef.current = incoming;
  const [rtcStatus, setRtcStatus] = useState("offline"); // offline | registering | registered | failed
  const [sipSession, setSipSession] = useState(null);
  const sipSessionRef = useRef(null);
  sipSessionRef.current = sipSession;
  const uaRef = useRef(null);
  const audioRef = useRef(null);

  const loadData = useCallback(() => {
    api.get("/extensions/mine").then((res) => { setExt(res.data); setLoaded(true); }).catch(() => setLoaded(true));
    api.get("/calls/mine").then((res) => setCalls(res.data)).catch(() => {});
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    let ws;
    let closed = false;
    let retry;
    const connect = () => {
      setWsStatus("connecting");
      ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onopen = () => setWsStatus("online");
      ws.onclose = () => {
        setWsStatus("offline");
        if (!closed) retry = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch (e) { return; }
        if (msg.type === "incoming_call") {
          setIncoming(msg);
          setInCall(false);
        } else if (msg.type === "call_ended") {
          if (incomingRef.current?.call_id === msg.call_id) {
            setIncoming(null);
            setInCall(false);
            toast.info("Call ended — no answer");
            loadData();
          }
        } else if (msg.type === "call_update") {
          if (incomingRef.current?.call_id === msg.call_id && (msg.status === "rejected" || msg.status === "completed")) {
            setIncoming(null);
            setInCall(false);
            loadData();
          }
        }
      };
    };
    connect();
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ type: "ping" }));
    }, 25000);
    return () => {
      closed = true;
      clearTimeout(retry);
      clearInterval(ping);
      ws?.close();
    };
  }, [loadData]);

  // Browser WebRTC phone: registers w<ext> on Asterisk via WSS (nginx proxies /ws).
  // Intercom calls ring the softphone AND the browser at the same time.
  useEffect(() => {
    if (!ext || !ext.webrtc_secret) return;
    let ua;
    try {
      const socket = new JsSIP.WebSocketInterface(`wss://${window.location.host}/ws`);
      ua = new JsSIP.UA({
        uri: `sip:w${ext.number}@${window.location.hostname}`,
        password: ext.webrtc_secret,
        sockets: [socket],
        register: true,
        register_expires: 300,
        session_timers: false,
      });
      ua.on("registered", () => setRtcStatus("registered"));
      ua.on("unregistered", () => setRtcStatus("offline"));
      ua.on("registrationFailed", () => setRtcStatus("failed"));
      ua.on("newRTCSession", (ev) => {
        if (ev.originator !== "remote") return;
        const s = ev.session;
        setSipSession(s);
        setIncoming({
          sip: true,
          call_id: `sip-${s.id}`,
          from_label: s.remote_identity?.display_name || s.remote_identity?.uri?.user || "Intercom",
          site_name: ext.site?.name || "",
          extension: ext.number,
        });
        setInCall(false);
        s.on("accepted", () => setInCall(true));
        const cleanup = () => { setSipSession(null); setIncoming(null); setInCall(false); loadData(); };
        s.on("ended", cleanup);
        s.on("failed", cleanup);
        if (s.connection) {
          s.connection.addEventListener("track", (t) => {
            if (audioRef.current && t.streams[0]) {
              audioRef.current.srcObject = t.streams[0];
              audioRef.current.play().catch(() => {});
            }
          });
        }
      });
      setRtcStatus("registering");
      ua.start();
      uaRef.current = ua;
    } catch (e) {
      setRtcStatus("failed");
    }
    return () => { try { ua?.stop(); } catch (e) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ext?.id]);

  const respond = (action) => {
    if (!incoming || incoming.sip || wsRef.current?.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "call_response", call_id: incoming.call_id, action }));
  };

  const onAccept = () => {
    const s = sipSessionRef.current;
    if (s) {
      s.answer({ mediaConstraints: { audio: true, video: false } });
      setInCall(true);
      toast.success("Call connected — live audio");
    } else {
      respond("accept");
      setInCall(true);
      toast.success("Call connected");
    }
  };
  const onReject = () => {
    const s = sipSessionRef.current;
    if (s) { try { s.terminate(); } catch (e) {} }
    else respond("reject");
    setSipSession(null);
    setIncoming(null);
    setInCall(false);
    loadData();
  };
  const onUnlock = async () => {
    const s = sipSessionRef.current;
    if (s && ext?.dtmf?.digit) {
      try {
        s.sendDTMF(ext.dtmf.digit, { transportType: ext.dtmf.type === "sip_info" ? "INFO" : "RFC2833" });
      } catch (e) {}
    }
    try {
      await api.post("/unlock");
      toast.success("Door latch triggered — entrance unlocked");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    respond("unlock");
    if (!sipSessionRef.current) setInCall(true);
  };
  const onEnd = () => {
    const s = sipSessionRef.current;
    if (s) { try { s.terminate(); } catch (e) {} }
    else respond("end");
    setSipSession(null);
    setIncoming(null);
    setInCall(false);
    loadData();
  };

  const testRing = async () => {
    try {
      await api.post("/extensions/mine/test-ring");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <Shell title="Tenant Portal">
      <IncomingCallModal call={incoming} inCall={inCall} onAccept={onAccept} onReject={onReject} onUnlock={onUnlock} onEnd={onEnd} />
      <audio ref={audioRef} autoPlay hidden data-testid="rtc-audio" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="tenant-portal">
        <div className="ops-card p-5 lg:col-span-2" data-testid="sip-status-widget">
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="ops-caption mb-1">My SIP Extension</div>
              {loaded && !ext && <p className="text-slate-400 text-sm">No extension assigned yet. Contact your administrator.</p>}
              {ext && (
                <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                  <Hash className="w-5 h-5 text-emerald-400" />
                  <span data-testid="my-extension-number">{ext.number}</span>
                  <span className="text-base font-medium text-slate-400">{ext.label}</span>
                </h2>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div
                data-testid="signaling-status"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-medium border ${
                  wsStatus === "online"
                    ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                    : wsStatus === "connecting"
                    ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
                    : "text-red-400 bg-red-500/10 border-red-500/30"}`}
              >
                {wsStatus === "online" ? <Signal className="w-3.5 h-3.5" /> : <SignalZero className="w-3.5 h-3.5" />}
                {wsStatus === "online" ? "ONLINE" : wsStatus === "connecting" ? "CONNECTING" : "OFFLINE"}
              </div>
              <div
                data-testid="rtc-status"
                title="Browser audio (WebRTC) — when ready, intercom calls ring this browser with live two-way audio"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono font-medium border ${
                  rtcStatus === "registered"
                    ? "text-cyan-400 bg-cyan-500/10 border-cyan-500/30"
                    : rtcStatus === "registering"
                    ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
                    : "text-slate-500 bg-slate-500/10 border-slate-500/30"}`}
              >
                <Smartphone className="w-3.5 h-3.5" />
                {rtcStatus === "registered" ? "AUDIO READY" : rtcStatus === "registering" ? "AUDIO…" : "AUDIO N/A"}
              </div>
            </div>
          </div>

          {ext && rtcStatus !== "registered" && rtcStatus !== "registering" && (
            <div className="mb-5 text-xs text-amber-400/90 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2"
              data-testid="rtc-hint">
              Browser audio is not connected — you will still see visual rings here, but no sound.
              Ask your administrator to confirm the latest package is deployed and the /ws proxy block is in the site config.
            </div>
          )}

          {ext && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-[#0A0D14] border border-[#1E2638] rounded-lg p-3">
                <div className="ops-caption mb-1 flex items-center gap-1.5"><Server className="w-3 h-3" /> SIP Server</div>
                <div className="font-mono text-sm text-slate-200" data-testid="my-server-domain">
                  {ext.server ? `${ext.server.domain}:${ext.server.port}` : "—"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{ext.server ? `${ext.server.name} • ${ext.server.transport}` : ""}</div>
              </div>
              <div className="bg-[#0A0D14] border border-[#1E2638] rounded-lg p-3">
                <div className="ops-caption mb-1 flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Site</div>
                <div className="text-sm text-slate-200" data-testid="my-site-name">{ext.site?.name || "—"}</div>
                <div className="text-xs text-slate-500 mt-0.5">{ext.site?.address || ""}</div>
              </div>
              <div className="bg-[#0A0D14] border border-[#1E2638] rounded-lg p-3">
                <div className="ops-caption mb-1">SIP Username</div>
                <button data-testid="copy-sip-username-btn" onClick={() => copy(`${ext.sip_username || ext.number}@${ext.server?.domain}`, "SIP username")}
                  className="font-mono text-sm text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1.5">
                  {ext.sip_username || ext.number}@{ext.server?.domain} <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="bg-[#0A0D14] border border-[#1E2638] rounded-lg p-3">
                <div className="ops-caption mb-1">SIP Secret</div>
                <button data-testid="copy-sip-secret-btn" onClick={() => copy(ext.sip_secret, "SIP secret")}
                  className="font-mono text-sm text-slate-300 hover:text-slate-100 inline-flex items-center gap-1.5">
                  •••••••••••• <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {ext && (
            <div className="mt-5 flex flex-wrap gap-3">
              <Button data-testid="test-ring-btn" onClick={testRing} variant="outline"
                className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 bg-transparent">
                <Zap className="w-4 h-4 mr-2" /> Test Ring Me
              </Button>
              <div className="text-xs text-slate-500 self-center">
                Incoming intercom calls ring here instantly while you're signed in.
              </div>
            </div>
          )}
        </div>

        <div className="ops-card p-5" data-testid="how-it-works-card">
          <div className="ops-caption mb-3 flex items-center gap-1.5"><PhoneIncoming className="w-3.5 h-3.5" /> How it works</div>
          <ol className="space-y-3 text-sm text-slate-300">
            <li className="flex gap-3"><span className="font-mono text-emerald-400">01</span> A visitor presses your unit on the building intercom.</li>
            <li className="flex gap-3"><span className="font-mono text-emerald-400">02</span> The intercom signals Scharpz Live! over the internet — no phone line involved.</li>
            <li className="flex gap-3"><span className="font-mono text-emerald-400">03</span> Your browser rings instantly. Accept, reject, or unlock the door remotely.</li>
          </ol>
          <div className="mt-5 bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3 text-xs text-cyan-300/80">
            Keep this tab open (or pinned) to stay reachable. Presence is live via secure WebSocket.
          </div>
        </div>
      </div>

      {ext && (
        <div className="ops-card p-5 mt-6" data-testid="softphone-setup-card">
          <div className="ops-caption mb-1 flex items-center gap-1.5">
            <Smartphone className="w-3.5 h-3.5" /> Softphone App (optional)
          </div>
          <h3 className="text-lg font-semibold text-slate-100 mb-1">Connect GSWave / Linphone</h3>
          <p className="text-sm text-slate-400 mb-4">
            Add these as a SIP account in your app (GSWave: Settings → Accounts → + → SIP Account).
            Intercom rings will reach both this page and the app, with real audio.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: "Server", value: ext.server?.domain || "" },
              { label: "Port", value: "5060" },
              { label: "Transport", value: "UDP" },
              { label: "Username", value: ext.sip_username || ext.number },
              { label: "Password", value: ext.sip_secret, secret: true },
            ].map((f) => (
              <div key={f.label} className="bg-[#0A0D14] border border-[#1E2638] rounded-lg p-3">
                <div className="ops-caption mb-1">{f.label}</div>
                <button
                  data-testid={`copy-softphone-${f.label.toLowerCase()}`}
                  onClick={() => copy(f.value, f.label)}
                  className="font-mono text-sm text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1.5 max-w-full"
                >
                  <span className="truncate">{f.secret ? "••••••••" : f.value}</span>
                  <Copy className="w-3.5 h-3.5 shrink-0" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ops-card p-5 mt-6" data-testid="call-history-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-400" /> Call History
          </h3>
          <Button data-testid="refresh-history-btn" variant="ghost" size="sm" onClick={loadData} className="text-slate-400 hover:text-slate-200">
            Refresh
          </Button>
        </div>
        {calls.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="call-history-empty">No calls yet. Use "Test Ring Me" to simulate an intercom ring.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left ops-caption border-b border-[#1E2638]">
                  <th className="pb-2 pr-4">Time</th>
                  <th className="pb-2 pr-4">From</th>
                  <th className="pb-2 pr-4">Ext</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <tr key={c.id} className="border-b border-[#1E2638]/50 last:border-0" data-testid={`call-row-${c.id}`}>
                    <td className="py-2.5 pr-4 text-slate-300 font-mono text-xs">
                      {format(new Date(c.created_at), "dd MMM yyyy HH:mm:ss")}
                    </td>
                    <td className="py-2.5 pr-4 text-slate-200">{c.from_label}</td>
                    <td className="py-2.5 pr-4 font-mono text-slate-400">{c.to_extension_number}</td>
                    <td className="py-2.5">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono border ${STATUS_STYLES[c.status] || STATUS_STYLES.missed}`}>
                        {c.status.replace("_", " ").toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}
